import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Cookies from "js-cookie";
import { motion } from "framer-motion";
import LoadingSearch from "./LoadingAnimasi";
import AspirasiForm from "./aspirasi/aspirasi";
import VoteSection from "./vote/vote";
import KinerjaSection from "./kinerja/kinerja";

interface Presiden {
  id: number;
  nama: string;
  jabatan: string;
  bidang: string;
  foto: string;
}

type TabType = "biografi" | "LHKPN" | "kinerja" | "aspirasi" | "vote";

const DetailPageUtama: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Presiden | null>(null);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState<TabType>("biografi");
  const [content, setContent] = useState<string | null>(null);
  const [loadingAI, setLoadingAI] = useState(false);

  // simpan cache dengan timestamp
  const setCache = (key: string, value: any) => {
    const payload = {
      value,
      savedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(payload));
  };

  const getCache = (key: string) => {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;
    const item = JSON.parse(itemStr);
    return item.value;
  };

  // interval auto-hapus tiap 10 menit
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const expireTime = 10 * 60 * 1000; // 10 menit

      Object.keys(localStorage).forEach((key) => {
        const itemStr = localStorage.getItem(key);
        if (!itemStr) return;
        try {
          const item = JSON.parse(itemStr);
          if (item.savedAt && now - item.savedAt > expireTime) {
            localStorage.removeItem(key);
          }
        } catch {
          // skip jika bukan JSON valid
        }
      });
    }, 60 * 1000); // cek tiap 1 menit

    return () => clearInterval(interval);
  }, []);

  // Fetch detail presiden
  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const token = Cookies.get("token");
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/presiden/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) throw new Error("Gagal ambil detail presiden");

        const result = await res.json();
        setData(result);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  // Fetch konten AI sesuai tab aktif
  useEffect(() => {
    if (!data) return;
    if (activeTab === "aspirasi" || activeTab === "vote") return; // skip untuk aspirasi & vote

    const fetchAIContent = async () => {
      const token = Cookies.get("token");
      const cacheKey = `detail-${id}-${activeTab}`;

      // cek cache dulu
      const cachedData = getCache(cacheKey);
      if (cachedData) {
        setContent(cachedData);
        return;
      }

      let prompt = "";
      if (activeTab === "biografi") {
        prompt = `Buat biografi tentang ${data.jabatan} ${data.nama} menggunakan informasi terbaru hingga tahun 2025 atau yang sekarang dari sumber terpercaya (Wikipedia atau situs resmi pemerintah).`;
      } else if (activeTab === "LHKPN") {
        prompt = `Ambil informasi terbaru tahun 2025 atau yang sekarang, tentang LHKPN (Laporan Harta Kekayaan Penyelenggara Negara) milik ${data.jabatan} ${data.nama} dari sumber resmi (KPK atau situs pemerintah).`;
      } else if (activeTab === "kinerja") {
        prompt = `Gunakan informasi terbaru hingga tahun 2025 dari sumber terpercaya (Wikipedia, portal berita resmi, atau situs pemerintah) untuk menjawab pertanyaan ini: apa yang baru dikerjakan oleh ${data.jabatan} ${data.nama}?`;
      }

      try {
        setLoadingAI(true);
        const resAI = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/openai-generate`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ prompt }),
        });

        if (resAI.ok) {
          const aiData = await resAI.json();
          const contentResult = aiData.choices?.[0]?.message?.content || null;
          setContent(contentResult);

          // simpan ke cache dengan timestamp
          if (contentResult) {
            setCache(cacheKey, contentResult);
          }
        } else {
          setContent("Gagal mengambil informasi dari AI.");
        }
      } catch (err) {
        console.error(err);
        setContent("Terjadi kesalahan saat mengambil informasi.");
      } finally {
        setLoadingAI(false);
      }
    };

    fetchAIContent();
  }, [activeTab, data, id]);

  if (loading) {
    return (
      <div>
        <LoadingSearch />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-10">Data tidak ditemukan</div>;
  }

  return (
    <div className="min-h-screen p-6 sm:p-10 bg-gradient-to-r from-white via-yellow-50 to-yellow-100">
      {/* Back button */}
      <Link to="/pageutama" className="inline-block mb-6 text-yellow-600 hover:underline">
        ← Kembali
      </Link>

      <div className="max-w-3xl mx-auto rounded-2xl">
        <div className="flex flex-col items-center">
          <div className="w-60 h-80 overflow-hidden rounded-2xl border-4 border-yellow-400 shadow-md">
            <img src={data.foto} alt={data.nama} className="w-full h-full object-cover" />
          </div>
          <h1 className="mt-6 text-2xl font-bold text-gray-800 text-center">{data.nama}</h1>
          <p className="text-lg text-gray-600 text-center">{data.jabatan}</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-between gap-3 mt-8 border-b border-gray-300 pb-2 flex-wrap">
          {["biografi", "LHKPN", "kinerja", "aspirasi", "vote"].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab as TabType)} className={`pb-2 text-lg font-medium capitalize ${activeTab === tab ? "border-b-2 border-yellow-600 text-yellow-600" : "text-gray-500 hover:text-yellow-600"}`}>
              {tab}
            </button>
          ))}
        </div>

        {/* Bagian Konten */}
        <div className="mt-6">
          {/* AI Content */}
          {["biografi", "LHKPN"].includes(activeTab) && (
            <>
              {loadingAI ? (
                <p className="text-center text-gray-500 mt-4">Mengambil informasi terbaru...</p>
              ) : content ? (
                <>
                  <div className="text-gray-700 flex justify-center mb-4 text-xl font-semibold">Hasil Informasi</div>
                  <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} className="mt-4 text-gray-800 whitespace-pre-line">
                    {content}
                  </motion.div>
                </>
              ) : (
                <p className="text-center text-gray-500 mt-4">Tidak ada informasi tambahan.</p>
              )}
            </>
          )}

          {activeTab === "kinerja" && <KinerjaSection presidenId={data.id} nama={data.nama} jabatan={data.jabatan} />}

          {/* Aspirasi */}
          {activeTab === "aspirasi" && <AspirasiForm presidenId={data.id} />}

          {/* Vote */}
          {activeTab === "vote" && <VoteSection presidenId={data.id} jabatan={data.jabatan} nama={data.nama} />}
        </div>
      </div>
    </div>
  );
};

export default DetailPageUtama;
